// Noise filter for the Sentry browser SDK.
//
// Google Tag Manager, the tags GTM injects and the Meta pixel are loaded from
// scripts we do not ship (see components/misc/CookieConsent.tsx). When a
// browser blocks them, fails to fetch them, or runs them half-loaded, the
// exception still reaches our global error handlers and arrives in Sentry
// looking like an application error. None of it is actionable — the fix would
// have to happen in someone else's script.
//
// The rule for adding anything here: drop an event only when its stack trace
// contains no frame we could edit. Filtering by message is deliberately
// avoided, because the browser reports a blocked GTM fetch and a failed order
// submit with the very same "Failed to fetch" / "Load failed" text, and the
// second one we very much want to see.

// Hostnames only ever match a frame that kept its origin. The SDK strips those
// (see THIRD_PARTY_PATHS), so in practice this list catches breadcrumb and
// request URLs, and paths do the work on stack traces.
const THIRD_PARTY_HOSTS = [
  "googletagmanager.com",
  "google-analytics.com",
  "googleadservices.com",
  "googlesyndication.com",
  "googletagservices.com",
  "doubleclick.net",
  "connect.facebook.net",
  "facebook.net",
];

// Extensions inject scripts into the page and throw from them constantly.
const EXTENSION_PROTOCOLS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "webkit-masked-url:",
];

// Some extensions do not throw from behind the protocols above. Wallets in
// particular add their provider to the page as a <script> tag, so the frame
// arrives stripped of the extension protocol and looks first-party — the event
// that prompted this list was "app:///evmAsk.js", one wallet extension failing
// to redefine window.ethereum after another had already claimed it. They are
// matched by file name, which is safe only because no file in this repo is or
// could be called any of these. Keep it that way when adding entries.
const INJECTED_SCRIPTS = [
  "evmAsk.js",
  "inpage.js",
  "injectedScript.js",
  // Facebook's in-app browser (FB_IAB, where most of our ad traffic lands)
  // injects its own performance logger and posts the results to the native app
  // over an Android JavaScript bridge. Once that bridge is released the posts
  // throw "Java object is gone" — inside Facebook's script, never inside ours.
  "navigation_performance_logger_android",
  // The same in-app browser also injects "promo_v2", which describes a clicked
  // element by walking up the DOM and joining class names into a CSS path. It
  // does not escape them, and Tailwind's container queries put an "@" in the
  // class attribute (@container on the order form's column, @lg: on its
  // fields), so it builds "div.@container > form.flex > ..." and its own
  // listener throws a SyntaxError on querySelector. Sentry's addEventListener
  // wrapper is what hands it to us; nothing of ours runs in that stack, and the
  // form submits regardless, because a throw in one listener does not stop the
  // others. The frame arrives as "app://promo_v2" — no file name, but the
  // double slash leaves it ending in "/promo_v2", which is what matches here.
  "promo_v2",
];

// Google's tag scripts are served from our own domain: CookieConsent.tsx loads
// GTM from googletagmanager.com, and that container then pulls gtag.js from the
// server-side tagging host gtm.travelalvaro.sk. When one of gtag's own POSTs is
// blocked, its promise rejects unhandled and ~50 frames of minified Google code
// arrive here.
//
// THIRD_PARTY_HOSTS cannot catch them. The Next.js SDK rewrites the origin of
// *every* stack frame to "app:///" before this file runs — third-party origins
// included, not just ours (build/cjs/client/clientNormalizationIntegration.js;
// the narrower behaviour is behind _experimental.thirdPartyOriginStackFrames in
// next.config.mjs). So "https://gtm.travelalvaro.sk/gtag/js" reaches us as
// "app:///gtag/js" with the hostname already gone, and the path is all that is
// left to match on.
//
// Safe for the same reason as INJECTED_SCRIPTS: none of these is a route this
// app serves or could serve. Keep it that way when adding entries.
const THIRD_PARTY_PATHS = ["/gtm.js", "/gtag/js", "/gtag/destination"];

const escape = (literal) => literal.replace(/\./g, "\\.");

/** Matched against the top stack frame by the SDK's inbound filters. */
export const THIRD_PARTY_URLS = [
  ...THIRD_PARTY_HOSTS.map((host) => new RegExp(escape(host))),
  ...EXTENSION_PROTOCOLS.map((protocol) => new RegExp(`^${protocol}`)),
  ...INJECTED_SCRIPTS.map((name) => new RegExp(`/${escape(name)}(?:[?#]|$)`)),
  ...THIRD_PARTY_PATHS.map((path) => new RegExp(`${escape(path)}(?:[?#]|$)`)),
];

/**
 * Messages that carry no usable stack trace, so the origin check below cannot
 * classify them. All of them come from outside the app.
 */
export const IGNORED_ERRORS = [
  // A cross-origin script threw. The browser withholds file, line and message,
  // so there is nothing to act on — this is the usual shape of a blocked or
  // partially loaded analytics script.
  "Script error.",
  // The GTM/pixel globals are missing because an ad blocker removed the script
  // before our call sites ran.
  /(?:can't find variable: |ReferenceError: )?(?:gtag|fbq|ga|_fbq|dataLayer) is not defined/i,
  /can't find variable: (?:gtag|fbq|ga|_fbq|dataLayer)/i,
  // Outlook's link scanner, not a real browser.
  "Object Not Found Matters Not",
  // Benign layout notification browsers emit; never a real failure.
  /^ResizeObserver loop/,
  // Android WebView wording for "the Java object behind this JS bridge has
  // been released", thrown when an in-app browser (Facebook's, above all)
  // tears its webview down while its own injected script is still posting to
  // it. This one is safe to match on text — the site owns no JS/Java bridge,
  // so the string cannot come from anywhere but a host app's own code.
  /Java object is gone/,
  /Java exception was raised during method invocation/,
  // The iOS twin of the two entries above. Facebook's in-app browser injects a
  // performance logger that posts LCP timings to the native app through the
  // WKWebView bridge at window.webkit.messageHandlers; on iOS 26 that object is
  // already gone when the callback fires, so it throws from its own
  // sendDataToNative/processLargestContentfulPaintEvent. The frames arrive as
  // "app:///:1:1325" — an inline script with no file name — so the origin check
  // below cannot classify them and the message is all that is left to match on.
  // Safe for the same reason as the Java bridge above: the site owns no native
  // bridge and never touches window.webkit (the WebkitMask* in our components
  // are CSS properties, not this object), so only a host app can produce it.
  /window\.webkit\.messageHandlers/,
  // The iOS half of the "promo_v2" entry in INJECTED_SCRIPTS: the same Facebook
  // in-app browser promo script (its HtmlDiffGenerator logs to
  // connect.facebook.net/promo_extension/log/ in the breadcrumbs) joins class
  // names into a CSS path without escaping them, so Tailwind's @container makes
  // "div.@container > form.flex > ..." and querySelector rejects it. On Android
  // the script carries its own file name and the origin check classifies it; on
  // iOS it runs inline in the document, so its frame arrives as
  // "app:///objednavka" — indistinguishable from the very route it injected
  // itself into — and the message is all that is left to match on.
  //
  // Safe because every selector this app hands to querySelector is a static
  // literal (DateField.tsx, HeaderMap.tsx, mapHelpers.ts — no interpolation at
  // any of them), so no call site of ours can raise this DOMException. Building
  // a selector at runtime would end that; keep them literal.
  /is not a valid selector/,
];

const isThirdPartyFrame = (frame) => {
  const file = frame.abs_path || frame.filename || "";
  // Injected inline/eval'd code carries no file we could open.
  if (!file || file === "<anonymous>") return true;
  const path = file.split(/[?#]/)[0];
  return (
    // The SDK's own wrappers (setTimeout, addEventListener, fetch) sit on the
    // stack of everything they instrument, third-party throws included. They
    // are not frames we could edit, so they must not keep an event alive on
    // their own — an application frame will still be there when the fault is
    // ours.
    file.includes("@sentry/") ||
    THIRD_PARTY_HOSTS.some((host) => file.includes(host)) ||
    EXTENSION_PROTOCOLS.some((protocol) => file.startsWith(protocol)) ||
    INJECTED_SCRIPTS.some((name) => path.endsWith(`/${name}`)) ||
    THIRD_PARTY_PATHS.some((tagPath) => path.endsWith(tagPath))
  );
};

/**
 * A fetch that dies at the transport layer rejects with a bare TypeError, and
 * WebKit hands that rejection no stack at all. The SDK fills one in from its
 * own instrumentation (@sentry/core, instrument/fetch.js), so the event arrives
 * with a stack pointing into the SDK and not one frame that says which call
 * site made the request — which is also why isThirdPartyFrame below cannot
 * classify it: in the browser those frames read "app:///_next/static/chunks/".
 *
 * The SDK does append the host to the message ("Load failed (cms.travelalvaro.sk)"),
 * but a host cannot tell a form submit apart from an image some in-app browser
 * decided to re-fetch on its own. The failing request is already in the event as
 * a breadcrumb, so lift its URL onto a tag: the path is the part that answers
 * the question, and a tag is visible in the issue list and searchable, where the
 * breadcrumb is buried in the event JSON.
 */
const NETWORK_TYPE_ERRORS =
  /^(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.)(?: \(|$)/;

function tagFailedFetchUrl(event) {
  const isNetworkFailure = (event.exception?.values ?? []).some(
    (value) =>
      value.type === "TypeError" && NETWORK_TYPE_ERRORS.test(value.value ?? ""),
  );
  if (!isNetworkFailure) return;

  // A call site that knows its URL tags it itself, and that beats the guess
  // below: Facebook's in-app browser drops every connection at once when it
  // closes, so the latest failed crumb is as likely to be GTM's /g/collect
  // beacon as the request that was actually reported.
  if (event.tags?.fetch_url) return;

  // The SDK adds this breadcrumb from the same rejection handler that throws,
  // so it is on the scope by the time the global handler builds the event.
  // Last one wins: with several requests in flight, the one that just failed is
  // the most recent error-level fetch crumb.
  // Walked by hand rather than with findLast, because this runs on every
  // visitor's browser and a missing method here would throw inside beforeSend,
  // which would cost us the event it was meant to describe.
  const crumbs = event.breadcrumbs ?? [];
  let url;
  for (let i = crumbs.length - 1; i >= 0; i--) {
    const crumb = crumbs[i];
    if (crumb.category === "fetch" && crumb.level === "error") {
      url = crumb.data?.url;
      break;
    }
  }

  if (typeof url === "string") {
    event.tags = { ...event.tags, fetch_url: url };
  }
}

/**
 * Catch-all for third-party errors whose top frame happens to be ours, or
 * whose frames are all extension/inline code. Runs after the URL and message
 * filters above.
 */
export function dropThirdPartyNoise(event) {
  tagFailedFetchUrl(event);

  // Anything our own code reported on purpose carries a source tag (order
  // submits, email delivery, price calculation). Those are kept unconditionally
  // — the message may look like third-party noise, the context never is.
  if (event.tags?.source) return event;

  const frames = (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? [],
  );

  // No stack at all (captureMessage, or a message filter already handled it).
  if (frames.length === 0) return event;

  return frames.every(isThirdPartyFrame) ? null : event;
}
